import { useCallback, useEffect, useRef, useState } from 'react';
import type { JSONContent } from '@tiptap/core';
import type {
  MapTranslationProjection,
  MapType,
} from '@course-authoring/shared-types';
import {
  useMapRegionTranslationMutation,
  useMapTopLevelTranslationMutation,
} from '../../api/mapApi';
import type { TranslationReplacementView } from '../../components/TranslationStatusPrimitives';

const EMPTY_DOC: JSONContent = { type: 'doc', content: [{ type: 'paragraph' }] };
const AUTOSAVE_INTERVAL_MS = 3000;
const SAVED_STATUS_DISPLAY_MS = 2000;
const TOP_LEVEL_RECORD_KEY = 'top-level';

export interface RegionEditorState {
  regionId: string;
  regionKey: string;
  name: string;
  description: JSONContent;
}

export interface MapEditorSnapshot {
  mapType: MapType;
  title: string;
  description: string;
  interactionMessage: string;
  regions: RegionEditorState[];
}

interface ReplacementDecision {
  recordKey: string;
  action: 'queue' | 'preserve';
}

export interface UseMapTranslationPersistenceOptions {
  mapId: string | undefined;
  selectedLocale: string;
  projection: MapTranslationProjection | undefined;
  getSnapshot: () => MapEditorSnapshot;
  hydrate: (snapshot: MapEditorSnapshot) => void;
  registerLocaleChangeGuard: (guard: () => Promise<boolean>) => () => void;
}

export function useMapTranslationPersistence({
  mapId,
  selectedLocale,
  projection,
  getSnapshot,
  hydrate,
  registerLocaleChangeGuard,
}: UseMapTranslationPersistenceOptions) {
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveErrors, setSaveErrors] = useState<Record<string, string>>({});
  const [replacementViews, setReplacementViews] =
    useState<Record<string, TranslationReplacementView>>({});
  const baselineRef = useRef<MapEditorSnapshot | null>(null);
  const hydratedScopeRef = useRef('');
  const inFlightRef = useRef<Promise<boolean> | null>(null);
  const persistOnUnmountRef = useRef<() => Promise<boolean>>(async () => true);
  const unmountFlushTimerRef = useRef<number | null>(null);
  const savedTimerRef = useRef<number | null>(null);
  const { mutateAsync: patchTopLevel, isPending: isSavingTopLevel } =
    useMapTopLevelTranslationMutation(mapId ?? '', selectedLocale);
  const { mutateAsync: patchRegion, isPending: isSavingRegion } =
    useMapRegionTranslationMutation(mapId ?? '', selectedLocale);

  useEffect(() => {
    const scope = `${mapId ?? ''}:${selectedLocale}`;
    if (
      !projection
      || projection.mapId !== mapId
      || projection.locale !== selectedLocale
      || hydratedScopeRef.current === scope
    ) return;
    const next: MapEditorSnapshot = {
      mapType: projection.mapType,
      title: projection.topLevel.fields.title,
      description: projection.topLevel.fields.description,
      interactionMessage: projection.topLevel.fields.interactionMessage,
      regions: projection.regions.map((region) => ({
        regionId: region.regionId,
        regionKey: region.regionKey,
        name: region.fields.name,
        description: parseRichText(region.fields.description),
      })),
    };
    hydrate(next);
    baselineRef.current = cloneSnapshot(next);
    hydratedScopeRef.current = scope;
    setSaveErrors({});
    setReplacementViews(() => {
      if (selectedLocale !== 'en') return {};
      const views: Record<string, TranslationReplacementView> = {};
      if (projection.topLevel.summary.pendingHumanTranslationCount > 0) {
        views[TOP_LEVEL_RECORD_KEY] = 'queued';
      }
      for (const region of projection.regions) {
        if (region.summary.pendingHumanTranslationCount > 0) {
          views[region.regionId] = 'queued';
        }
      }
      return views;
    });
    setSaveStatus('idle');
  }, [hydrate, mapId, projection, selectedLocale]);

  useEffect(() => {
    if (unmountFlushTimerRef.current !== null) {
      window.clearTimeout(unmountFlushTimerRef.current);
      unmountFlushTimerRef.current = null;
    }
    return () => {
      if (savedTimerRef.current !== null) window.clearTimeout(savedTimerRef.current);
      // React Strict Mode immediately re-runs effects after a simulated cleanup.
      // Deferring lets that setup cancel the task while a real unmount still flushes.
      unmountFlushTimerRef.current = window.setTimeout(() => {
        void persistOnUnmountRef.current();
      }, 0);
    };
  }, []);

  const beginSave = useCallback(() => {
    if (savedTimerRef.current !== null) window.clearTimeout(savedTimerRef.current);
    setSaveStatus('saving');
  }, []);

  const markSaved = useCallback(() => {
    setSaveStatus('saved');
    if (savedTimerRef.current !== null) window.clearTimeout(savedTimerRef.current);
    savedTimerRef.current = window.setTimeout(
      () => setSaveStatus('idle'),
      SAVED_STATUS_DISPLAY_MS,
    );
  }, []);

  const markFailed = useCallback(() => {
    if (savedTimerRef.current !== null) window.clearTimeout(savedTimerRef.current);
    setSaveStatus('error');
  }, []);

  const persistChanges = useCallback(async function persistMapChanges(
    decision?: ReplacementDecision,
  ): Promise<boolean> {
    if (inFlightRef.current) {
      await inFlightRef.current;
      return persistMapChanges(decision);
    }
    const scope = `${mapId ?? ''}:${selectedLocale}`;
    if (
      !mapId
      || !baselineRef.current
      || hydratedScopeRef.current !== scope
    ) return false;
    const current = getSnapshot();
    const baseline = baselineRef.current;
    const topChanged =
      current.title !== baseline.title
      || current.description !== baseline.description
      || current.interactionMessage !== baseline.interactionMessage;
    const changedRegions = current.regions.filter((region) => {
      const previous = baseline.regions.find(
        (candidate) => candidate.regionId === region.regionId,
      );
      return !previous
        || previous.name !== region.name
        || JSON.stringify(previous.description) !== JSON.stringify(region.description);
    });
    const decisionRegion = decision && decision.recordKey !== TOP_LEVEL_RECORD_KEY
      ? current.regions.find((region) => region.regionId === decision.recordKey)
      : undefined;
    if (!topChanged && changedRegions.length === 0 && !decision) return true;

    const savePromise = (async () => {
      beginSave();
      setSaveErrors({});
      const nextBaseline = cloneSnapshot(baseline);
      const errors: Record<string, string> = {};

      if (topChanged || decision?.recordKey === TOP_LEVEL_RECORD_KEY) {
        try {
          await patchTopLevel({
            ...(topChanged ? {
              fields: {
                title: current.title,
                description: current.description,
                interactionMessage: current.interactionMessage,
              },
            } : {}),
            ...(decision?.recordKey === TOP_LEVEL_RECORD_KEY
              ? { replacementAction: decision.action }
              : {}),
          });
          nextBaseline.title = current.title;
          nextBaseline.description = current.description;
          nextBaseline.interactionMessage = current.interactionMessage;
        } catch (error) {
          errors[TOP_LEVEL_RECORD_KEY] = getErrorMessage(error, 'Top-level map save failed');
        }
      }

      const regionsToSave = [...changedRegions];
      if (
        decisionRegion
        && !regionsToSave.some((region) => region.regionId === decisionRegion.regionId)
      ) {
        regionsToSave.push(decisionRegion);
      }
      for (const region of regionsToSave) {
        const changed = changedRegions.some(
          (candidate) => candidate.regionId === region.regionId,
        );
        try {
          await patchRegion({
            regionId: region.regionId,
            patch: {
              ...(changed
                ? { fields: { name: region.name, description: region.description } }
                : {}),
              ...(decision?.recordKey === region.regionId
                ? { replacementAction: decision.action }
                : {}),
            },
          });
          const index = nextBaseline.regions.findIndex(
            (candidate) => candidate.regionId === region.regionId,
          );
          if (index >= 0) {
            nextBaseline.regions[index] = cloneRegion(region);
          }
        } catch (error) {
          errors[region.regionId] = getErrorMessage(error, 'Region save failed');
        }
      }

      if (hydratedScopeRef.current !== scope) return true;
      baselineRef.current = nextBaseline;
      setSaveErrors(errors);
      if (Object.keys(errors).length > 0) {
        markFailed();
        return false;
      }
      markSaved();
      return true;
    })();
    inFlightRef.current = savePromise;
    try {
      return await savePromise;
    } finally {
      if (inFlightRef.current === savePromise) inFlightRef.current = null;
    }
  }, [
    beginSave,
    getSnapshot,
    mapId,
    markFailed,
    markSaved,
    patchRegion,
    patchTopLevel,
    selectedLocale,
  ]);
  persistOnUnmountRef.current = persistChanges;

  useEffect(() => {
    if (
      !baselineRef.current
      || inFlightRef.current
      || hydratedScopeRef.current !== `${mapId ?? ''}:${selectedLocale}`
    ) return;
    if (JSON.stringify(getSnapshot()) === JSON.stringify(baselineRef.current)) return;
    const timeout = window.setTimeout(() => void persistChanges(), AUTOSAVE_INTERVAL_MS);
    return () => window.clearTimeout(timeout);
  }, [getSnapshot, mapId, persistChanges, saveStatus, selectedLocale]);

  useEffect(
    () => registerLocaleChangeGuard(() => persistChanges()),
    [persistChanges, registerLocaleChangeGuard],
  );

  const promptForReplacement = useCallback((recordKey: string, humanTranslationCount: number) => {
    if (selectedLocale !== 'en' || humanTranslationCount < 1) return;
    setReplacementViews((current) => (
      current[recordKey] ? current : { ...current, [recordKey]: 'prompt' }
    ));
  }, [selectedLocale]);

  const applyReplacementDecision = useCallback(async (
    recordKey: string,
    action: ReplacementDecision['action'],
  ) => {
    const succeeded = await persistChanges({ recordKey, action });
    if (succeeded) {
      setReplacementViews((current) => ({
        ...current,
        [recordKey]: action === 'queue' ? 'queued' : 'preserved',
      }));
    }
  }, [persistChanges]);

  return {
    saveStatus,
    saveErrors,
    replacementViews,
    persistChanges,
    promptForReplacement,
    showReplacementPrompt: useCallback((recordKey: string) => {
      setReplacementViews((current) => ({ ...current, [recordKey]: 'prompt' }));
    }, []),
    applyReplacementDecision,
    beginSave,
    markSaved,
    markFailed,
    resetHydration: useCallback(() => {
      hydratedScopeRef.current = '';
    }, []),
    isSavingTranslation: isSavingTopLevel || isSavingRegion,
  };
}

function parseRichText(raw: string | JSONContent): JSONContent {
  let parsed: JSONContent;
  if (typeof raw === 'object') {
    parsed = raw;
  } else {
    try {
      parsed = JSON.parse(raw) as JSONContent;
    } catch {
      return EMPTY_DOC;
    }
  }
  return parsed && parsed.type === 'doc' ? parsed : EMPTY_DOC;
}

function cloneSnapshot(snapshot: MapEditorSnapshot): MapEditorSnapshot {
  return JSON.parse(JSON.stringify(snapshot)) as MapEditorSnapshot;
}

function cloneRegion(region: RegionEditorState): RegionEditorState {
  return JSON.parse(JSON.stringify(region)) as RegionEditorState;
}

function getErrorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
    return error.message;
  }
  return fallback;
}
